/**
 * 看板与审计计划与服务器双向同步（按用户隔离）
 * 拉取/写入时携带当前用户 ID，服务端按 users/<userId>/ 存储，实现不同用户数据互不覆盖
 */

import { toast } from '../components/Toast.jsx';

const API_BASE = '/api/database/files';
const KANBAN_FILE = 'audit_kanban_data.json';
const PLANS_FILE = 'audit_plans_data.json';
const IT_AUDIT_FILE = 'it_audit_data.json';
const ENGINEERING_AUDIT_FILE = 'engineering_audit_data.json';
const KANBAN_KEY = 'audit_kanban_data';
const PLANS_KEY = 'audit_plans_data';
const USER_STORAGE_KEY = 'audit_portal_user';
const IS_TEST_ENV = (
  (typeof process !== 'undefined' && !!process?.env?.VITEST) ||
  (typeof process !== 'undefined' && process?.env?.NODE_ENV === 'test')
);

function resolveApiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return new URL(path, window.location.origin).toString();
    }
  } catch {}
  return path;
}

/** IT审计 localStorage 键（与 itAuditService 一致） */
const IT_AUDIT_KEYS = [
  'it_audit_projects',
  'it_audit_checklist_templates',
  'it_audit_checklists',
  'it_audit_systems',
  'it_audit_data_governance',
  'it_audit_findings',
  'it_audit_ticket_reviews',
  'it_audit_ops_material_checked',
  'it_audit_ops_plan_custom',
  'it_audit_ops_gantt_custom',
];

/** 工程审计 localStorage 键（与 engineeringAuditService / EngAuditCapexReview 一致），均持久化到服务器 */
const ENGINEERING_AUDIT_KEYS = [
  'engineering_audit_projects',
  'engineering_audit_contracts',
  'engineering_audit_costs',
  'engineering_audit_visas',
  'engineering_audit_settlements',
  'engineering_audit_findings',
  'engineering_audit_notices',
  'engineering_audit_plans',
  'engineering_audit_site_inspections',
  'engineering_audit_reports',
  'engineering_audit_seal_applications',
  'engineering_audit_doubts',
  'engineering_audit_project_materials',
  'engineering_audit_entry_applications',
  'engineering_audit_procurement_filings',
  'engineering_audit_rectification_progress',
  'engineering_audit_patrol_records',
  'engineering_audit_bid_supervision',
  'engineering_capex_planning',           // CAPEX 投资规划表
  'engineering_capex_audit_checklist',   // CAPEX 审计检查清单
];

/** 从 localStorage 获取当前登录用户 ID，用于按用户隔离看板/计划 */
function getCurrentUserId() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(USER_STORAGE_KEY) : null;
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user && user.id ? String(user.id) : null;
  } catch {
    return null;
  }
}

/** 从 localStorage 获取 JWT（登录后保存），用于 /api/database/files 等需认证的接口 */
function getAuthToken() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  } catch {
    return null;
  }
}

/** 看板/计划请求头：带当前用户 ID 与认证令牌，以便服务端校验并按用户存储 */
function getPerUserHeaders() {
  const userId = getCurrentUserId();
  const token = getAuthToken();
  const headers = { Pragma: 'no-cache', 'Cache-Control': 'no-cache' };
  if (userId) headers['X-User-Id'] = userId;
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

const fetchFromServer = async (filename, { timeoutMs = 8000, retries = 1 } = {}) => {
  if (IS_TEST_ENV) return null;
  const url = resolveApiUrl(`${API_BASE}/${filename}`);
  const headers = getPerUserHeaders();
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    let timeoutId = null;
    try {
      if (attempt > 0) await new Promise((r) => setTimeout(r, 1500));
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, {
        signal: controller.signal,
        cache: 'no-store',
        headers,
        credentials: 'include',
      });
      if (!res.ok) return null;
      const json = await res.json();
      return json.data;
    } catch (e) {
      if (attempt === retries) return null;
    } finally {
      if (timeoutId != null) clearTimeout(timeoutId);
    }
  }
  return null;
};

/**
 * @returns {{ ok: boolean, status?: number, message?: string, skipped?: boolean }}
 */
const saveToServer = async (filename, data) => {
  if (IS_TEST_ENV) return { ok: true, skipped: true };
  const controller = new AbortController();
  let timeoutId;
  const headers = { 'Content-Type': 'application/json', ...getPerUserHeaders() };
  try {
    timeoutId = setTimeout(() => controller.abort(), 8000);
    const res = await fetch(resolveApiUrl(`${API_BASE}/${filename}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ data }),
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'include',
    });
    if (!res.ok) {
      let message = '';
      try {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const j = await res.json();
          message = (j && (j.error || j.message)) ? String(j.error || j.message) : '';
        }
      } catch (_) {}
      console.warn(`[serverDataSync] PUT ${filename} 失败: ${res.status}`, message || '');
      return { ok: false, status: res.status, message: message || undefined };
    }
    return { ok: true };
  } catch (e) {
    console.warn('[serverDataSync] 写入服务器失败', e?.message || e);
    return { ok: false, status: 0, message: e?.name === 'AbortError' ? '同步超时，请检查网络' : (e?.message || '') };
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }
};

/** 看板/计划同步失败时用于 Toast 的文案（ok / skipped 时不返回） */
export function getServerSyncFailureMessage(result) {
  if (!result || result.ok || result.skipped) return '';
  if (result.status === 401) return '未登录或登录已过期，数据未能保存到服务器';
  if (result.status === 413) return '同步数据过大被网关拒绝：请在 Nginx 中增大 client_max_body_size（建议≥50m），或删除部分历史工单审核记录后重试';
  if (result.message) return result.message;
  if (result.status === 403) return '没有权限写入服务器，请确认账号权限';
  if (result.status === 0) return '无法连接服务器，请检查网络后重试';
  return `保存到服务器失败（HTTP ${result.status}）`;
}

/**
 * 从服务器拉取看板与审计计划并覆盖本地 localStorage（多端一致）
 */
export const fetchAndApplyKanbanAndPlans = async () => {
  try {
    const [kanbanData, plansData] = await Promise.all([
      fetchFromServer(KANBAN_FILE),
      fetchFromServer(PLANS_FILE),
    ]);
    if (kanbanData != null && typeof kanbanData === 'object') {
      localStorage.setItem(KANBAN_KEY, JSON.stringify(kanbanData));
    }
    if (plansData != null && Array.isArray(plansData)) {
      localStorage.setItem(PLANS_KEY, JSON.stringify(plansData));
    }
  } catch (e) {
    console.warn('[serverDataSync] 拉取看板/计划失败', e?.message || e);
  }
};

/**
 * 将本地看板数据同步到服务器（在每次本地保存后调用）
 */
export const syncKanbanToServer = async () => {
  try {
    const raw = localStorage.getItem(KANBAN_KEY);
    if (raw == null) return { ok: true, skipped: true };
    const data = JSON.parse(raw);
    return await saveToServer(KANBAN_FILE, data);
  } catch (e) {
    console.warn('[serverDataSync] 同步看板到服务器失败', e?.message || e);
    return { ok: false, status: 0, message: e?.message };
  }
};

/**
 * 将本地审计计划数据同步到服务器（在每次本地保存后调用）
 */
export const syncPlansToServer = async () => {
  try {
    const raw = localStorage.getItem(PLANS_KEY);
    if (raw == null) return { ok: true, skipped: true };
    const data = JSON.parse(raw);
    return await saveToServer(PLANS_FILE, data);
  } catch (e) {
    console.warn('[serverDataSync] 同步计划到服务器失败', e?.message || e);
    return { ok: false, status: 0, message: e?.message };
  }
};

async function notifySyncFailureToast(result) {
  const msg = getServerSyncFailureMessage(result);
  if (!msg) return;
  try {
    toast.warning(msg);
  } catch (_) {
    /* Toast 未挂载时忽略 */
  }
}

/** 同步看板到服务器；失败时 Toast 提示（项目管理/整改等模块共用） */
export const syncKanbanToServerWithFeedback = async () => {
  const result = await syncKanbanToServer();
  await notifySyncFailureToast(result);
  return result;
};

/** 同步审计计划到服务器；失败时 Toast 提示 */
export const syncPlansToServerWithFeedback = async () => {
  const result = await syncPlansToServer();
  await notifySyncFailureToast(result);
  return result;
};

/**
 * 从服务器拉取IT审计数据并写入 localStorage
 */
export const fetchAndApplyITAudit = async () => {
  try {
    const data = await fetchFromServer(IT_AUDIT_FILE);
    if (data != null && typeof data === 'object') {
      IT_AUDIT_KEYS.forEach((key) => {
        if (data[key] !== undefined) {
          try {
            localStorage.setItem(key, JSON.stringify(data[key]));
          } catch (e) {
            console.warn('[serverDataSync] IT审计写入本地失败', key, e?.message);
          }
        }
      });
    }
  } catch (e) {
    console.warn('[serverDataSync] 拉取IT审计数据失败', e?.message || e);
  }
};

/**
 * 将本地IT审计数据同步到服务器（itAuditService 在每次保存后调用）
 * @returns {Promise<{ ok: boolean, status?: number, message?: string, skipped?: boolean }>}
 */
export const syncITAuditToServer = async () => {
  try {
    const data = {};
    IT_AUDIT_KEYS.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        if (raw != null) data[key] = JSON.parse(raw);
      } catch (e) {
        console.warn('[serverDataSync] IT审计读取本地失败', key, e?.message);
      }
    });
    return await saveToServer(IT_AUDIT_FILE, data);
  } catch (e) {
    console.warn('[serverDataSync] 同步IT审计到服务器失败', e?.message || e);
    return { ok: false, status: 0, message: e?.message || String(e) };
  }
};

/** 同步 IT 审计到服务器；失败时 Toast（含工单审核等模块） */
export const syncITAuditToServerWithFeedback = async () => {
  const result = await syncITAuditToServer();
  await notifySyncFailureToast(result);
  return result;
};

/**
 * 从服务器拉取工程审计数据并写入 localStorage
 */
export const fetchAndApplyEngineeringAudit = async () => {
  try {
    const data = await fetchFromServer(ENGINEERING_AUDIT_FILE);
    if (data != null && typeof data === 'object') {
      ENGINEERING_AUDIT_KEYS.forEach((key) => {
        if (data[key] !== undefined) {
          try {
            localStorage.setItem(key, JSON.stringify(data[key]));
          } catch (e) {
            console.warn('[serverDataSync] 工程审计写入本地失败', key, e?.message);
          }
        }
      });
    }
  } catch (e) {
    console.warn('[serverDataSync] 拉取工程审计数据失败', e?.message || e);
  }
};

/**
 * 将本地工程审计数据同步到服务器（共享存储，其他用户登录后可见）
 * @returns {Promise<{ ok: boolean, status?: number }>} ok 表示成功，status 为 HTTP 状态码（失败时，401 表示未登录）
 */
export const syncEngineeringAuditToServer = async () => {
  try {
    const data = {};
    ENGINEERING_AUDIT_KEYS.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        if (raw != null) data[key] = JSON.parse(raw);
      } catch (e) {
        console.warn('[serverDataSync] 工程审计读取本地失败', key, e?.message);
      }
    });
    const result = await saveToServer(ENGINEERING_AUDIT_FILE, data);
    return result;
  } catch (e) {
    console.warn('[serverDataSync] 同步工程审计到服务器失败', e?.message || e);
    return { ok: false, status: 0 };
  }
};
